import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { MainLayoutComponent } from '../../_layouts/main-layout/main-layout.component';
import { AlertService } from '../../_services/alert.service';
import { AdminProductList, AdminProductRow, MinPriceFixResult, ProductService } from '../../_services/product.service';
import { ViewModalComponent } from './view-modal/view-modal.component';
import { UserService } from '../../_services/users.service';
import { CmsService } from '../../_services/cms.service';
import { CmsCurrentlyEditing } from '../../_interfaces/cms';
import { cmsContentTypes } from '../../../Shared/_constants/constants';
import { environment } from '../../../../environments/environment';
import { PaginatorPagesDirective } from '../../../Shared/_directives/paginator-pages.directive';

type ViewKey = 'ALL' | 'ACTIVE' | 'INACTIVE' | 'IN' | 'LOW' | 'OUT' | 'OFFER';

@Component({
    selector: 'app-products',
    imports: [PaginatorPagesDirective,
        MainLayoutComponent,
        MatPaginatorModule,
        MatIconModule,
        MatMenuModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        CommonModule,
        MatButtonModule,
        FormsModule,
    ],
    templateUrl: './products.component.html',
    styleUrl: './products.component.css'
})
export class ProductsComponent implements OnInit, OnDestroy {
  readonly dialog = inject(MatDialog);
  frontEndURL = environment.frontEndURL;
  readonly Math = Math;

  products: AdminProductRow[] = [];
  result: AdminProductList | null = null;
  loading = true;
  loadError = false;
  userInfo: any;

  prodKeyword = '';
  view: ViewKey = 'ALL';
  sort = 'NAME';
  pageNumber = 0;
  itemsPerPage = 20;
  count = 0;

  readonly sortOptions = [
    { key: 'NAME', label: 'Name A-Z' },
    { key: 'NEWEST', label: 'Newest first' },
    { key: 'PRICE_ASC', label: 'Price: low to high' },
    { key: 'PRICE_DESC', label: 'Price: high to low' },
    { key: 'STOCK_ASC', label: 'Stock: lowest first' },
    { key: 'STOCK_DESC', label: 'Stock: highest first' },
  ];

  private typed = new Subject<string>();
  private sub?: Subscription;

  // ADMIN or S-ADMIN. Roles can be a comma-joined list, so this checks
  // membership - a plain == 'ADMIN' missed S-ADMIN (and multi-role users).
  get isAdmin(): boolean {
    const roles = String(this.userInfo?.userrole ?? '').split(',').map((r) => r.trim());
    return roles.includes('ADMIN') || roles.includes('S-ADMIN');
  }

  constructor(
    private productService: ProductService,
    private router: Router,
    private userService: UserService,
    private alertService: AlertService,
    private cmsService: CmsService,
  ) {}

  ngOnInit(): void {
    this.userInfo = this.userService.isLoggedIn();
    // Search as you type, once typing pauses.
    this.sub = this.typed.pipe(debounceTime(350), distinctUntilChanged()).subscribe(() => this.onSearch());
    this.loadProducts();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  // ---- "Fix From prices": the price shown as "From £..." on the website ----
  fixOpen = false;
  fixLoading = false;
  fixApplying = false;
  fixResult: MinPriceFixResult | null = null;

  openFixPrices() {
    this.fixOpen = true;
    this.fixResult = null;
    this.fixLoading = true;
    this.productService.recalculateMinPrices(false).subscribe({
      next: (r) => { this.fixResult = r; this.fixLoading = false; },
      error: (error) => {
        this.fixLoading = false;
        this.fixOpen = false;
        this.alertService.alert(error?.error?.message || 'Could not check the prices', 'error');
      },
    });
  }

  applyFixPrices() {
    if (!this.fixResult?.changes.length || this.fixApplying) return;
    this.fixApplying = true;
    this.productService.recalculateMinPrices(true).subscribe({
      next: (r) => {
        this.fixApplying = false;
        this.fixOpen = false;
        this.alertService.alert(`Updated the "From" price on ${r.changes.length} product${r.changes.length === 1 ? '' : 's'}`, 'success');
        this.loadProducts();
      },
      error: (error) => {
        this.fixApplying = false;
        this.alertService.alert(error?.error?.message || 'Could not update the prices', 'error');
      },
    });
  }

  // ---- the filter chips ----
  get views(): { key: ViewKey; label: string; icon: string; count: number | undefined; tone: string }[] {
    const c = this.result?.counts;
    return [
      { key: 'ALL', label: 'All', icon: 'apps', count: c?.total, tone: 'neutral' },
      { key: 'ACTIVE', label: 'Active', icon: 'check_circle', count: c?.active, tone: 'green' },
      { key: 'INACTIVE', label: 'Inactive', icon: 'pause_circle', count: c?.inactive, tone: 'grey' },
      { key: 'IN', label: 'In stock', icon: 'inventory_2', count: c?.inStock, tone: 'green' },
      { key: 'LOW', label: 'Low stock', icon: 'trending_down', count: c?.lowStock, tone: 'orange' },
      { key: 'OUT', label: 'Out of stock', icon: 'block', count: c?.outOfStock, tone: 'red' },
      { key: 'OFFER', label: 'On offer', icon: 'sell', count: c?.onOffer, tone: 'rose' },
    ];
  }

  setView(key: ViewKey) {
    this.view = key;
    this.pageNumber = 0;
    this.loadProducts();
  }

  onKeywordChange(value: string) {
    this.prodKeyword = value;
    this.typed.next(value.trim());
  }

  onSearch() {
    this.pageNumber = 0;
    this.loadProducts();
  }

  clearSearch() {
    this.prodKeyword = '';
    this.onSearch();
  }

  setSort(key: string) {
    this.sort = key;
    this.pageNumber = 0;
    this.loadProducts();
  }

  get sortLabel(): string {
    return this.sortOptions.find((o) => o.key === this.sort)?.label ?? 'Sort';
  }

  loadProducts() {
    this.loading = true;
    this.loadError = false;
    const filter: Record<ViewKey, { status?: string; stock?: string; offer?: boolean }> = {
      ALL: {}, ACTIVE: { status: 'ACTIVE' }, INACTIVE: { status: 'INACTIVE' },
      IN: { stock: 'IN' }, LOW: { stock: 'LOW' }, OUT: { stock: 'OUT' }, OFFER: { offer: true },
    };
    this.productService
      .getProductsAdminList({
        keywordSearch: this.prodKeyword.trim(),
        sort: this.sort,
        pageIndex: this.pageNumber,
        pageSize: this.itemsPerPage,
        ...filter[this.view],
      })
      .subscribe({
        next: (response) => {
          this.result = response;
          this.products = response.products;
          this.count = response.count;
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
          this.loadError = true;
          this.alertService.alert(error?.error?.message || 'Could not load products', 'error');
        },
      });
  }

  get hasFilters(): boolean {
    return !!this.prodKeyword.trim() || this.view !== 'ALL';
  }

  clearFilters() {
    this.prodKeyword = '';
    this.view = 'ALL';
    this.onSearch();
  }

  get rangeLabel(): string {
    if (!this.count) return '';
    const from = this.pageNumber * this.itemsPerPage + 1;
    return `Showing ${from}-${Math.min(from + this.products.length - 1, this.count)} of ${this.count}`;
  }

  // ---- row helpers ----
  stockState(p: AdminProductRow): { label: string; cls: string } {
    if (p.stock <= 0) return { label: 'Out of stock', cls: 'bg-red-50 text-red-700' };
    if (p.stock <= (this.result?.lowStockLimit ?? 5)) return { label: 'Low stock', cls: 'bg-orange-50 text-orange-700' };
    return { label: 'In stock', cls: 'bg-emerald-50 text-emerald-700' };
  }

  skuPreview(p: AdminProductRow): { first: string; more: number } {
    const list = String(p.skus ?? '').split(',').map((s) => s.trim()).filter(Boolean);
    return { first: list[0] ?? '', more: Math.max(0, list.length - 1) };
  }

  // ---- actions ----
  onAddProduct() {
    this.router.navigate(['/products/create']);
  }

  onEditClick(rowData: any) {
    this.router.navigate(['/products/edit', rowData.productSlug]);
  }

  onOpenCMS(rowData: any) {
    const cmsEditingContent: CmsCurrentlyEditing = {
      type: cmsContentTypes.PRODUCT_DESCRIPTION,
      contentData: rowData,
    };
    this.cmsService.setEditingContent(cmsEditingContent);
    this.router.navigate(['/cms']);
  }

  onView(rowData: any) {
    this.dialog.open(ViewModalComponent, { width: '80%', data: { ...rowData } });
  }

  public updateList(event: PageEvent): void {
    if (this.itemsPerPage !== event.pageSize) {
      this.itemsPerPage = event.pageSize || 20;
      this.pageNumber = 0;
    } else {
      this.pageNumber = event.pageIndex;
    }
    this.loadProducts();
  }
}
